import { ThemeEvents } from '@theme/events';
import { addToCart, cartSectionIds } from '@theme/ca-cart-add';

/** <gift-wrapping>: adds or removes the gift-wrap product; morph-safe, reads cart state fresh. */
(function () {
  if (customElements.get('gift-wrapping')) return;

  const SOURCE_ID = 'gift-wrapping';
  let orphanRemoving = false;

  class GiftWrapping extends HTMLElement {
    connectedCallback() {
      if (this.__wired) return; // morph preserves the node — only wire once
      this.__wired = true;

      this.busy = false;
      this.dialogOpen = false;
      this.closeAction = null;
      this.lastSaved = null;

      this.onChange = this.onChange.bind(this);
      this.onClick = this.onClick.bind(this);
      this.onDialogClose = this.onDialogClose.bind(this);
      this.onCartUpdate = this.onCartUpdate.bind(this);
      this.onDialogKeydown = this.onDialogKeydown.bind(this);

      this.addEventListener('change', this.onChange);
      this.addEventListener('click', this.onClick);
      document.addEventListener(ThemeEvents.cartUpdate, this.onCartUpdate);

      this.addEventListener('keydown', this.onDialogKeydown);

      setTimeout(() => this.checkOrphan(), 0);
    }

    disconnectedCallback() {
      this.removeEventListener('change', this.onChange);
      this.removeEventListener('click', this.onClick);
      this.removeEventListener('keydown', this.onDialogKeydown);
      document.removeEventListener(ThemeEvents.cartUpdate, this.onCartUpdate);
    }

    /* ---- fresh-read accessors (never cache cart state) ---------------------- */

    get variantId() {
      return this.dataset.variantId || '';
    }
    get addUrl() {
      return this.dataset.addUrl || '/cart/add.js';
    }
    get changeUrl() {
      return this.dataset.changeUrl || '/cart/change.js';
    }
    get propertyKey() {
      return this.dataset.propertyKey || 'Gift message';
    }
    get lineKey() {
      return this.dataset.giftLineKey || '';
    }
    set lineKey(value) {
      this.dataset.giftLineKey = value || '';
    }
    get showMessage() {
      return this.dataset.showMessage === 'true';
    }
    get dialog() {
      return this.querySelector('[data-giftwrap-dialog]');
    }
    get textarea() {
      return this.querySelector('[data-giftwrap-message]');
    }
    get checkbox() {
      return this.querySelector('[data-giftwrap-checkbox]');
    }

    /* ---- helpers ----------------------------------------------------------- */

    announce(message) {
      const live = this.querySelector('[data-giftwrap-live]');
      if (live) live.textContent = message;
    }

    setBusy(state) {
      this.busy = state;
      const cb = this.checkbox;
      if (cb) cb.disabled = state;
    }

    dispatchUpdate(sections) {
      if (!sections) return;
      document.dispatchEvent(
        new CustomEvent(ThemeEvents.cartUpdate, {
          bubbles: true,
          detail: {
            resource: null,
            sourceId: SOURCE_ID,
            data: { source: SOURCE_ID, sections: sections },
          },
        })
      );
    }

    post(url, payload) {
      return fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(payload),
      }).then((res) => {
        if (!res.ok) throw new Error('cart request failed');
        return res.json();
      });
    }

    insertPendingRow() {
      try {
        const scope = this.closest('cart-items-component') || document;
        const tbody = scope.querySelector('.cart-items__table tbody');
        if (!tbody) return null;

        const src = this.dataset.wrapImage || '';
        const row = document.createElement('tr');
        row.className = 'ca-cart-pending';
        row.setAttribute('data-ca-pending', '');
        row.setAttribute('aria-hidden', 'true');

        const cell = document.createElement('td');
        cell.className = 'ca-cart-pending__inner';
        const thumb = document.createElement('span');
        thumb.className = 'ca-cart-pending__thumb';
        if (src) {
          const im = document.createElement('img');
          im.src = src;
          im.alt = '';
          im.loading = 'lazy';
          thumb.appendChild(im);
        }
        const lines = document.createElement('span');
        lines.className = 'ca-cart-pending__lines';
        const titleBar = document.createElement('span');
        titleBar.className = 'ca-cart-pending__bar ca-cart-pending__bar--title';
        const priceBar = document.createElement('span');
        priceBar.className = 'ca-cart-pending__bar ca-cart-pending__bar--price';
        lines.append(titleBar, priceBar);
        const spinner = document.createElement('span');
        spinner.className = 'ca-cart-pending__spinner';
        cell.append(thumb, lines, spinner);
        row.appendChild(cell);

        tbody.prepend(row);
        return row;
      } catch (e) {
        return null;
      }
    }

    /* ---- add / remove ------------------------------------------------------ */

    onChange(event) {
      if (!event.target.matches('[data-giftwrap-checkbox]')) return;
      if (this.busy) {
        event.target.checked = !event.target.checked;
        return;
      }
      if (event.target.checked) this.addWrap();
      else this.removeWrap();
    }

    onClick(event) {
      if (event.target.closest('[data-giftwrap-edit]')) {
        if (this.showMessage) this.openDialog();
        return;
      }
      if (event.target.closest('[data-giftwrap-dialog-save]')) {
        this.closeDialog('save');
        return;
      }
      if (
        event.target.closest('[data-giftwrap-dialog-decline]') ||
        event.target.closest('[data-giftwrap-dialog-close]')
      ) {
        this.closeDialog('decline');
        return;
      }
      const dlg = this.dialog;
      if (dlg && dlg.open && event.target === dlg) this.closeDialog('decline');
    }

    addWrap() {
      const variantId = this.variantId;
      if (!variantId) return;
      this.setBusy(true);
      this.lastSaved = '';
      const pending = this.insertPendingRow();

      addToCart({ id: variantId, quantity: 1, url: this.addUrl })
        .then(({ response, data }) => {
          if (!response.ok || !data) throw new Error('cart request failed');
          return data;
        })
        .then((data) => {
          const item = (data.items && data.items[0]) || data;
          if (item && item.key) this.lineKey = item.key;
          if (pending) pending.remove();
          this.setBusy(false);
          this.announce('Gift wrapping added to your order');
          this.dispatchUpdate(data.sections);
          if (this.showMessage) this.openDialog();
        })
        .catch(() => {
          if (pending) pending.remove();
          const cb = this.checkbox;
          if (cb) cb.checked = false;
          this.setBusy(false);
          this.announce('Could not add gift wrapping. Please try again.');
        });
    }

    removeWrap() {
      const key = this.lineKey;
      if (!key) {
        this.setBusy(false);
        orphanRemoving = false;
        return;
      }
      this.setBusy(true);
      this.post(this.changeUrl, {
        id: key,
        quantity: 0,
        sections: cartSectionIds().join(','),
        sections_url: window.location.pathname,
      })
        .then((data) => {
          this.lineKey = '';
          this.lastSaved = null;
          this.setBusy(false);
          orphanRemoving = false;
          this.announce('Gift wrapping removed');
          this.dispatchUpdate(data.sections);
        })
        .catch(() => {
          const cb = this.checkbox;
          if (cb) cb.checked = true;
          this.setBusy(false);
          orphanRemoving = false;
          this.announce('Could not remove gift wrapping. Please try again.');
        });
    }

    /* ---- orphan guard ------------------------------------------------------ */

    onCartUpdate(event) {
      if (event && event.detail && event.detail.sourceId === SOURCE_ID) return;
      setTimeout(() => this.checkOrphan(), 0);
    }

    checkOrphan() {
      if (!this.isConnected || this.busy || orphanRemoving) return;
      if (this.dataset.orphaned !== 'true' || !this.lineKey) return;
      orphanRemoving = true;
      this.removeWrap();
    }

    /* ---- message popup ----------------------------------------------------- */

    openDialog() {
      const dlg = this.dialog;
      if (!dlg) return;
      this.closeAction = 'decline';
      if (!dlg.open) {
        try {
          dlg.showModal();
        } catch (e) {
          return;
        }
        dlg.addEventListener('close', this.onDialogClose, { once: true });
      }
      this.dialogOpen = true;
      const ta = this.textarea;
      if (ta) ta.focus();
    }

    closeDialog(action) {
      this.closeAction = action;
      const dlg = this.dialog;
      if (dlg && dlg.open) dlg.close();
      else this.onDialogClose();
    }

    onDialogClose() {
      this.dialogOpen = false;
      const action = this.closeAction || 'decline';
      this.closeAction = null;
      if (action === 'save') this.saveMessage();
    }

    dialogTabbables() {
      const dlg = this.dialog;
      if (!dlg) return [];
      const sel = 'a[href], button, input, select, textarea, [tabindex]';
      return Array.from(dlg.querySelectorAll(sel)).filter((el) => {
        if (el.hasAttribute('disabled')) return false;
        if (el.getAttribute('tabindex') === '-1') return false;
        if (el.closest('[hidden]')) return false;
        return el.offsetWidth > 0 || el.offsetHeight > 0 || el === document.activeElement;
      });
    }

    // Nested modal dialog: manual Tab trap, and stop Escape so the drawer's own handler doesn't close the whole drawer.
    onDialogKeydown(event) {
      const dlg = this.dialog;
      if (!dlg || !dlg.open) return;
      if (!dlg.contains(event.target)) return;

      if (event.key === 'Escape') {
        event.preventDefault();
        event.stopPropagation();
        this.closeDialog('decline');
        return;
      }
      if (event.key !== 'Tab') return;
      const items = this.dialogTabbables();
      if (!items.length) return;
      const first = items[0];
      const last = items[items.length - 1];
      const active = document.activeElement;
      if (event.shiftKey) {
        if (active === first || !dlg.contains(active)) {
          event.preventDefault();
          last.focus();
        }
      } else if (active === last || !dlg.contains(active)) {
        event.preventDefault();
        first.focus();
      }
    }

    saveMessage() {
      if (!this.isConnected) return;
      const key = this.lineKey;
      if (!key) return;
      const ta = this.textarea;
      const value = ta ? ta.value : '';
      if (value === this.lastSaved) return;

      // An empty message must clear the property (Shopify keeps empty strings).
      const properties = {};
      if (value.trim() !== '') properties[this.propertyKey] = value;

      this.post(this.changeUrl, {
        id: key,
        properties: properties,
        sections: cartSectionIds().join(','),
        sections_url: window.location.pathname,
      })
        .then((data) => {
          this.lastSaved = value;
          const line = (data.items || []).find(
            (i) => String(i.variant_id) === String(this.variantId)
          );
          if (line && line.key) this.lineKey = line.key;
          this.dispatchUpdate(data.sections);
        })
        .catch(() => {
          this.announce('Could not save your gift message. Please try again.');
        });
    }
  }

  customElements.define('gift-wrapping', GiftWrapping);
})();
